import { AA_NON_TEXT, AA_TEXT, contrastRatio } from "./contrast.js";

/**
 * Jetons de design.
 *
 * La charte est décrite dans docs/CHARTE.md : **sérigraphie par défaut, identité
 * du client quand il paie**. Ce module en est la traduction en valeurs, partagée
 * par l'application et le back-office — une seule définition, deux surfaces.
 *
 * La séparation en deux moitiés n'est pas cosmétique, elle porte une règle :
 *
 * - les **encres** sont substituables — c'est par elles qu'un organisateur rend
 *   l'application sienne ;
 * - les **mesures** ne le sont pas — échelles typographiques, espacements,
 *   tailles de cible tactile. Un client peut rendre l'application
 *   méconnaissable, jamais illisible ni inatteignable au doigt.
 *
 * Même logique que le verrouillage des chaînes de sécurité en traduction
 * (INTERNATIONAL.md §3).
 */

/**
 * Encres d'un thème.
 *
 * Le vocabulaire est celui de l'impression, et il n'est pas décoratif : en
 * sérigraphie on ne dispose que de quelques encres posées en aplats sur un
 * papier. Cette contrainte est ce qui donne son allure au résultat, et nommer
 * les jetons ainsi empêche d'en ajouter une douzaine sans y penser.
 */
export type Inks = {
  /** Fond de l'écran. */
  paper: string;
  /** Surface posée sur le fond : carte, champ de saisie, panneau. */
  paperRaised: string;
  /** Texte courant. */
  ink: string;
  /** Texte secondaire — toujours lisible, jamais décoratif. */
  inkMuted: string;
  /** Encre d'accentuation : aplats, boutons, sélection. */
  accent: string;
  /** Texte posé sur un aplat d'accentuation. */
  onAccent: string;
  /** Seconde encre, pour les mises en avant qui ne sont pas des actions. */
  highlight: string;
  /** Texte posé sur la seconde encre. */
  onHighlight: string;
  /** Ce qui a mal tourné. */
  alert: string;
  /** Texte posé sur un aplat d'alerte. */
  onAlert: string;
  /**
   * Filet décoratif : séparation entre deux blocs.
   *
   * Sans exigence de contraste, et c'est volontaire : le critère 1.4.11 des
   * WCAG vise ce qui **porte une information**. Un trait qui ne fait que séparer
   * n'en porte aucune — le forcer à 3:1 produirait des lignes noires en travers
   * de chaque écran.
   */
  rule: string;
  /**
   * Bordure porteuse de sens : contour d'un champ, limite d'une zone cliquable.
   *
   * Celle-ci **doit** atteindre 3:1 : elle est parfois le seul indice qu'il y a
   * là quelque chose à remplir.
   */
  edge: string;
};

export type Theme = {
  /** Identifiant du thème, utilisé pour le choisir et le journaliser. */
  name: string;
  inks: Inks;
};

/**
 * Thème de jour : l'affiche.
 *
 * Papier crème et encres franches. C'est ce que voient la plupart des
 * festivals — ceux qui n'ont ni direction artistique ni budget pour en imposer
 * une — donc ce n'est pas un pis-aller.
 */
export const PAPER_THEME: Theme = {
  name: "papier",
  inks: {
    // Un papier presque blanc, à peine chaud. La version précédente tirait
    // franchement sur le beige : posé sur un écran de téléphone, cela se lit
    // comme du jauni, pas comme du papier.
    paper: "#fbf8f3",
    paperRaised: "#ffffff",
    ink: "#1a1815",
    inkMuted: "#6b655c",
    accent: "#b03a24",
    onAccent: "#ffffff",
    highlight: "#e6a94a",
    onHighlight: "#1a1815",
    alert: "#a32218",
    onAlert: "#ffffff",
    // Un filet à peine visible : il sépare, il ne souligne pas.
    rule: "#eae4da",
    edge: "#8a8175",
  },
};

/**
 * Thème de nuit : la même affiche, tirée en négatif.
 *
 * Un festival se vit après la tombée du jour, et c'est précisément là qu'on
 * cherche les toilettes ou ses amis. Un écran crème en pleine figure à deux
 * heures du matin éblouit et détruit la vision nocturne — ce n'est pas une
 * préférence, c'est une question d'usage.
 */
export const NIGHT_THEME: Theme = {
  name: "nuit",
  inks: {
    paper: "#131211",
    paperRaised: "#1d1c1a",
    ink: "#f5f2ec",
    inkMuted: "#a9a49b",
    accent: "#f0724f",
    onAccent: "#131211",
    highlight: "#e6a94a",
    onHighlight: "#131211",
    alert: "#ff8080",
    onAlert: "#131211",
    rule: "#2a2825",
    edge: "#7a736a",
  },
};

/** Thèmes livrés. Un thème de locataire s'y ajoutera sans les remplacer. */
export const THEMES: readonly Theme[] = [PAPER_THEME, NIGHT_THEME];

/**
 * Embedded typefaces: the poster style chosen on 2026-10-13 (CHARTE.md).
 *
 * Archivo (SIL OFL 1.1), two static cuts: Extra-Condensed Black for screen
 * titles, Condensed Extra-Bold for headings, buttons and labels. Running text
 * stays in the system face, which reads best at small sizes. The names are the
 * PostScript names, which is what both platforms and the back-office use to
 * find the face.
 */
export const FONT_FACES = {
  display: "ArchivoExtraCondensed-Black",
  heading: "ArchivoCondensed-ExtraBold",
} as const;

/**
 * Échelle typographique.
 *
 * Peu de tailles, très contrastées : c'est ce qui fait l'affiche. Une échelle
 * fine et régulière donne des écrans mous où rien ne ressort.
 *
 * The two largest sizes are set in condensed faces, which take far less width:
 * hence a display size that would be too big in the system face.
 *
 * Non surchargeable par un locataire.
 */
export const TYPE = {
  /** Titre d'écran, une ligne, très large. */
  display: 44,
  title: 26,
  heading: 18,
  body: 16,
  label: 14,
  caption: 13,
} as const;

/**
 * Interlignes.
 *
 * Une valeur par taille, et non un multiplicateur unique : un titre serré et un
 * paragraphe aéré n'ont pas le même rapport. C'est ce qui distingue un écran
 * composé d'un écran empilé.
 */
export const LINE_HEIGHT = {
  // Capitals only: no descenders, so a tight leading.
  display: 46,
  title: 30,
  heading: 24,
  body: 23,
  label: 20,
  caption: 18,
} as const;

/**
 * Graisses.
 *
 * Trois, et non deux : le demi-gras porte les titres de section et les libellés,
 * là où le gras franc écrasait tout. Un écran où tout ce qui compte est en gras
 * n'a plus de hiérarchie.
 */
export const WEIGHT = {
  regular: "400",
  medium: "500",
  bold: "700",
} as const;

/**
 * Espacements, en multiples de quatre.
 *
 * Non surchargeable : c'est ce qui tient la lisibilité d'un écran, et un
 * locataire n'a aucune raison de le resserrer.
 */
export const SPACE = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  /** Séparation entre deux sections d'un même écran. */
  section: 40,
} as const;

/**
 * Rayons.
 *
 * Nearly square: a screen-printed poster has cut edges. The softer rounding
 * of the previous versions was dropped with the poster style (2026-10-13). The
 * pill stays for what is round by nature: an avatar, a dot.
 */
export const RADIUS = {
  sm: 2,
  md: 3,
  lg: 4,
  pill: 999,
} as const;

/**
 * Taille minimale d'une cible tactile.
 *
 * Quarante-huit points, et non les vingt-quatre du minimum des WCAG : on vise
 * un usage debout, dans la foule, une main occupée par un gobelet.
 *
 * Non surchargeable.
 */
export const TOUCH_TARGET = 48;

/**
 * Épaisseur des filets et bordures.
 *
 * Ink strokes, as in the poster style: two points around what can be touched,
 * three for the rules that open a section or close a bar.
 */
export const STROKE = {
  hairline: 1,
  /** Contour d'un élément qu'on peut toucher. */
  edge: 2,
  /** Section rule, top and bottom bars. */
  bold: 3,
} as const;

/**
 * Paires de couleurs qui doivent tenir dans tout thème.
 *
 * Cette liste est la définition opérationnelle de « lisible » : ce qui n'y
 * figure pas n'est pas garanti, ce qui y figure ne peut pas régresser — ni dans
 * un thème livré, ni dans celui d'un organisateur.
 */
const TEXT_PAIRS: [keyof Inks, keyof Inks, string][] = [
  ["ink", "paper", "texte courant sur le fond"],
  ["ink", "paperRaised", "texte courant sur une surface posée"],
  ["inkMuted", "paper", "texte secondaire sur le fond"],
  ["inkMuted", "paperRaised", "texte secondaire sur une surface posée"],
  ["accent", "paper", "encre d'accentuation en texte"],
  ["onAccent", "accent", "texte sur un aplat d'accentuation"],
  ["onHighlight", "highlight", "texte sur la seconde encre"],
  ["alert", "paper", "message d'erreur"],
  ["onAlert", "alert", "texte sur un aplat d'alerte"],
];

/** Bordures porteuses d'information : critère 1.4.11 des WCAG. */
const EDGE_PAIRS: [keyof Inks, keyof Inks, string][] = [
  ["edge", "paper", "contour de champ sur le fond"],
  ["edge", "paperRaised", "contour de champ sur une surface posée"],
];

/**
 * Contrôle d'accessibilité d'un thème, rendant la liste des manquements.
 *
 * Le même code sert aux thèmes livrés — vérifiés en intégration continue — et à
 * celui d'un organisateur, refusé à l'enregistrement s'il échoue. C'est la
 * traduction en code de la règle de docs/CHARTE.md : un client peut rendre
 * l'application méconnaissable, jamais illisible.
 */
export function auditTheme(theme: Theme): string[] {
  const problems: string[] = [];

  for (const [foreground, background, label] of TEXT_PAIRS) {
    const ratio = contrastRatio(theme.inks[foreground], theme.inks[background]);
    if (ratio < AA_TEXT) {
      problems.push(`${label} : ${ratio.toFixed(2)} au lieu de ${AA_TEXT} minimum`);
    }
  }

  for (const [foreground, background, label] of EDGE_PAIRS) {
    const ratio = contrastRatio(theme.inks[foreground], theme.inks[background]);
    if (ratio < AA_NON_TEXT) {
      problems.push(`${label} : ${ratio.toFixed(2)} au lieu de ${AA_NON_TEXT} minimum`);
    }
  }

  return problems;
}

/** Vrai si toutes les couleurs sont des valeurs hexadécimales exploitables. */
export function isInks(value: unknown): value is Inks {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;

  return (Object.keys(PAPER_THEME.inks) as (keyof Inks)[]).every(
    (key) =>
      typeof candidate[key] === "string" &&
      /^#[0-9a-fA-F]{6}$/.test(candidate[key] as string),
  );
}

/**
 * Thème d'un organisateur, appliqué par-dessus le nôtre.
 *
 * Les encres seules sont substituées : échelles typographiques, espacements et
 * tailles de cible tactile restent les nôtres, quel que soit l'abonnement.
 */
export function tenantTheme(name: string, inks: Inks): Theme {
  return { name, inks };
}
